import type { AxiosInstance } from "axios";
import type { TokenManager } from "../../auth/token-manager";
import { installStudioAuth } from "./studio-auth";
import { installStudioNamespace } from "./studio-namespace";
import { installStudioRetry, type StudioRetryOptions } from "./studio-retry";

export interface StudioApiHandles {
  auth: number;
  namespace: number;
  retry: number;
}

/**
 * Wire an axios instance for the Studio in one call.
 *
 * Registration order is load-bearing and therefore lives HERE, not at call
 * sites: axios runs request interceptors in REVERSE registration order
 * (LIFO), so auth is registered first and namespace second — making the
 * namespace/capability gate run BEFORE auth. A request for an unshipped
 * endpoint group must be refused before it triggers a token round-trip.
 * The integration test's "ordering proof" case is the tripwire for this.
 *
 * Plan 2C calls this from the Studio entry point against the shared
 * axiosInstance (after the dashboard's own interceptors are deleted), with
 * the SAME TokenManager it hands to EditorAuthProvider — one manager, one
 * token, one refresh lifecycle.
 */
export function installStudioApi(
  instance: AxiosInstance,
  manager: TokenManager,
  options: StudioRetryOptions = {},
): StudioApiHandles {
  const auth = installStudioAuth(instance, manager);
  const namespace = installStudioNamespace(instance);
  const retry = installStudioRetry(instance, manager, options);
  return { auth, namespace, retry };
}
